import { type ReactElement, type RefObject, useCallback, useEffect, useState } from 'react';
import { faXmark } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { Measurable } from '@radix-ui/rect';

import { ButtonEditorForm } from '@/components/blocks/shared/button-editor-form';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Heading5 } from '@/components/ui/typography';
import { usePopoverPlacement } from '@/hooks/use-popover-placement';
import { isPageLinkType } from '@/lib/blocks/button-links';
import { cn } from '@/lib/utils';
import type { PageBlock } from '@/types/block';
import type { ButtonConfig } from '@/types/button';
import { useBlockButtonDraftSync } from './use-block-button-draft-sync';

export { BUTTON_TYPE_OPTIONS } from '@/components/blocks/shared/button-editor-form';

interface ButtonEditorPopoverProps {
  initialValue: ButtonConfig;
  trigger?: ReactElement;
  onChange?: (value: ButtonConfig) => void;
  onSave: (value: ButtonConfig) => void | Promise<void>;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  anchorRef?: RefObject<Measurable | null>;
  title?: string;
  disabled?: boolean;
}

interface BlockButtonEditorPopoverProps {
  sectionId: string;
  block: PageBlock;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef?: RefObject<Measurable | null>;
  title?: string;
  disabled?: boolean;
}

export function ButtonEditorPopover({
  initialValue,
  trigger,
  onChange,
  onSave,
  onOpenChange,
  open: controlledOpen,
  anchorRef,
  title = 'Button',
  disabled = false,
}: ButtonEditorPopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [latestDraft, setLatestDraft] = useState<ButtonConfig>(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const { side, sideOffset } = usePopoverPlacement(anchorRef, open, 450, 0.5);

  useEffect(() => {
    if (!open) setLatestDraft(initialValue);
  }, [initialValue, open]);

  const linkType = latestDraft.linkType || 'external';
  const hasValidDestination = isPageLinkType(linkType)
    ? Boolean(latestDraft.internalPageId)
    : Boolean(latestDraft.link?.trim());

  const handleDraftChange = useCallback(
    (next: ButtonConfig) => {
      setLatestDraft(next);
      onChange?.(next);
    },
    [onChange],
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (controlledOpen === undefined) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await onSave(latestDraft);
      handleOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {anchorRef ? <PopoverAnchor virtualRef={anchorRef as RefObject<Measurable>} /> : null}
      {trigger ? <PopoverTrigger asChild>{trigger}</PopoverTrigger> : null}
      <PopoverContent
        side={side}
        align="start"
        sideOffset={sideOffset}
        avoidCollisions
        collisionPadding={16}
        hideWhenDetached
        className={cn('z-[130] w-96 overflow-hidden border bg-background')}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onInteractOutside={(event) => {
          const target = event.target;
          if (!(target instanceof Element)) {
            return;
          }

          if (target.closest('[data-slot="select-content"]')) {
            event.preventDefault();
          }
        }}
      >
        <Button
          size="icon"
          variant="ghost"
          colorScheme="secondary"
          onClick={() => handleOpenChange(false)}
          disabled={isSaving}
          className="h-8 w-8 absolute top-4 right-4 z-50 bg-background"
        >
          <Icon icon={faXmark} />
          <span className="sr-only">Close</span>
        </Button>
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1 max-h-[min(50vh,450px)] overflow-y-auto space-y-4 px-2">
            <Heading5 className="text-base mb-4">{title}</Heading5>
            <ButtonEditorForm
              initialValue={initialValue}
              resetKey={open}
              enableSearch={open}
              onChange={handleDraftChange}
            />
            <div className="flex justify-end space-x-4 pt-4 mt-4 sticky bottom-0 bg-background">
              <Button
                variant="outline"
                colorScheme="secondary"
                onClick={() => handleOpenChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                isLoading={isSaving}
                disabled={disabled || !hasValidDestination}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function BlockButtonEditorPopover({
  sectionId,
  block,
  open,
  onOpenChange,
  anchorRef,
  title = 'Button',
  disabled = false,
}: BlockButtonEditorPopoverProps) {
  const { initialValue, handleDraftChange, handleDraftSave, handleOpenChange } =
    useBlockButtonDraftSync({
      sectionId,
      block,
      open,
      onOpenChange,
    });

  return (
    <ButtonEditorPopover
      open={open}
      anchorRef={anchorRef}
      initialValue={initialValue}
      onChange={handleDraftChange}
      onSave={handleDraftSave}
      onOpenChange={handleOpenChange}
      title={title}
      disabled={disabled}
    />
  );
}
